import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi, type ContentOwner, type CourseFile } from '@/lib/api';

/*
 * Uploading a course file: ask the API for an upload URL, PUT the file
 * straight to storage (XHR, for progress), complete it, then wait while it's
 * scanned for malware.
 */

export interface UploadState {
  filename: string;
  /** 0–1 while bytes are moving. */
  progress: number;
  phase: 'uploading' | 'checking' | 'done' | 'error';
  error?: string;
}

export function put(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress: (p: number) => void,
) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    xhr.send(file);
  });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useUpload(owner: ContentOwner, courseId?: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [state, setState] = useState<UploadState | null>(null);

  const upload = useCallback(
    async (file: File): Promise<CourseFile | null> => {
      setState({ filename: file.name, progress: 0, phase: 'uploading' });
      try {
        const { asset, upload: target } = await api.requestUpload(owner, {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          courseId: courseId ?? null,
        });
        await put(target.url, target.headers, file, (progress) =>
          setState({ filename: file.name, progress, phase: 'uploading' }),
        );
        setState({ filename: file.name, progress: 1, phase: 'checking' });
        let done = await api.completeUpload(asset.id);
        // Malware scanning usually takes a few seconds; don't wait forever.
        for (let i = 0; i < 40 && done.status === 'scanning'; i++) {
          await sleep(i < 10 ? 1500 : 4000);
          done = await api.courseFile(asset.id);
        }
        await queryClient.invalidateQueries({ queryKey: ['courseFiles'] });
        if (done.status === 'infected')
          throw new Error(`${file.name} was flagged as unsafe and removed.`);
        if (done.status === 'failed')
          throw new Error(`${file.name} couldn't be checked. Try uploading it again.`);
        setState({ filename: file.name, progress: 1, phase: 'done' });
        return done;
      } catch (err) {
        setState({
          filename: file.name,
          progress: 0,
          phase: 'error',
          error: err instanceof Error ? err.message : String(err),
        });
        return null;
      }
    },
    [api, owner, courseId, queryClient],
  );

  return { upload, state, reset: () => setState(null) };
}
